import type { ArdoqId, EnhancedPresentation } from '@ardoq/api-types';
import { isTruthy } from '@ardoq/common-helpers';
import { dispatchAction } from '@ardoq/rxbeach';
import { dispatchActionAndWaitForResponse } from 'actions/utils';
import Cookie from 'cookie_js';
import isEqual from 'lodash/isEqual';
import {
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  type Subscription,
} from 'rxjs';
import {
  createBrowserRouterAdapter,
  type ArrivedLocation,
} from 'router/declarative/browserRouterAdapter';
import { DeclarativeRouter } from 'router/declarative/declarativeRouter';
import type { LocationIn } from 'router/declarative/location';
import type { History } from 'router/history';
import {
  loadPresentation,
  loadPresentationError,
  loadPresentationSuccess,
} from './actions';
import {
  getPresentationRouteTree,
  invalidPresentationRouteError,
  isMatchedPresentationLocation,
  type CustomDomainPresentationLocation,
  type CustomDomainPresentationRouteTree,
  type PresentationRouteTree,
  type StandardDomainPresentationLocation,
  type StandardDomainPresentationRouteTree,
  toLoadPresentationPayload,
  toPresentationLocation,
} from './presentationRoutes';
import { isPptxExportRunning, pptxExport$ } from './pptxExport/pptxExport$';
import { selectSlide } from './slideNavigator/actions';
import { slideNavigator$ } from './slideNavigator/slideNavigator$';
import { currentOrganization$ } from './streams/currentOrganization$';
import { loadedPresentation$ } from './streams/loadedPresentation$';

type SetupRouterOptions = {
  isCustomDomain: boolean;
  browserHistory: History;
};

type PresentationRouter<Tree extends PresentationRouteTree> = {
  presentationRouter: DeclarativeRouter<Tree>;
  startRouter: () => Promise<void>;
  teardown: VoidFunction;
};

type PresentationDomain<Tree extends PresentationRouteTree> = {
  routeTree: Tree;
  toStateLocation: (state: PresentationState) => LocationIn<Tree> | null;
};

type PresentationState = {
  loadedPresentationId: ArdoqId | null;
  loadedSlideId: ArdoqId | null;
  selectedSlideId: ArdoqId | null;
  organizationLabel: string | null;
  contextComponentId: ArdoqId | null;
};

const toCustomDomainStateLocation = ({
  loadedPresentationId,
  loadedSlideId,
  selectedSlideId,
  contextComponentId,
}: PresentationState): CustomDomainPresentationLocation | null => {
  if (!loadedPresentationId) return null;

  return toPresentationLocation({
    isCustomDomain: true,
    organizationLabel: null,
    presentationId: loadedPresentationId,
    selectedSlideId: selectedSlideId || loadedSlideId,
    contextComponentId,
  });
};

const toStandardDomainStateLocation = ({
  loadedPresentationId,
  loadedSlideId,
  selectedSlideId,
  organizationLabel,
  contextComponentId,
}: PresentationState): StandardDomainPresentationLocation | null => {
  if (!loadedPresentationId) return null;

  const activeSlideId = selectedSlideId || loadedSlideId;

  if (organizationLabel) {
    return toPresentationLocation({
      isCustomDomain: false,
      organizationLabel,
      presentationId: loadedPresentationId,
      selectedSlideId: activeSlideId,
      contextComponentId,
    });
  }

  if (!activeSlideId) return null;

  return toPresentationLocation({
    isCustomDomain: false,
    organizationLabel: null,
    presentationId: loadedPresentationId,
    selectedSlideId: activeSlideId,
    contextComponentId,
  });
};

const presentationStateLocation$ = <Tree extends PresentationRouteTree>(
  toStateLocation: (state: PresentationState) => LocationIn<Tree> | null
) =>
  combineLatest([
    loadedPresentation$,
    slideNavigator$,
    currentOrganization$,
    pptxExport$,
  ]).pipe(
    filter(([, , , pptxExport]) => !isPptxExportRunning(pptxExport)),
    map(
      ([
        { loadedPresentationId, loadedSlideId, contextComponentId },
        { selectedSlideId },
        currentOrganization,
      ]): LocationIn<Tree> | null =>
        toStateLocation({
          loadedPresentationId,
          loadedSlideId,
          selectedSlideId,
          organizationLabel: currentOrganization?.label ?? null,
          contextComponentId,
        })
    ),
    filter(isTruthy),
    distinctUntilChanged(isEqual)
  );

const setOrganizationCookie = (organizationLabel: string | null): void => {
  Cookie.set({ organization: organizationLabel }, { path: '/', secure: true });
};

const createPresentationRouter = <Tree extends PresentationRouteTree>(
  { routeTree, toStateLocation }: PresentationDomain<Tree>,
  browserHistory: History
): PresentationRouter<Tree> => {
  const presentationRouter = new DeclarativeRouter<Tree>(routeTree);
  let browserLocationsBeingApplied = 0;
  let pendingStateLocation: LocationIn<Tree> | null = null;
  let stateSubscription: Subscription | null = null;
  let loadedPresentation: EnhancedPresentation | null = null;
  let loadedContextComponentId: ArdoqId | null = null;
  let loadedPresentationSubscription: Subscription | null = null;

  const departOrDeferStateLocation = (location: LocationIn<Tree>): void => {
    if (browserLocationsBeingApplied > 0) {
      pendingStateLocation = location;
      return;
    }
    presentationRouter.depart(location);
  };

  const loadPresentationFromLocation = async (
    location: ArrivedLocation<Tree>
  ): Promise<void> => {
    if (!isMatchedPresentationLocation(location)) {
      throw invalidPresentationRouteError();
    }

    const payload = toLoadPresentationPayload(location);
    if (!payload.isCustomDomain) {
      setOrganizationCookie(payload.organizationLabel);
    }

    const canReuseLoadedPresentation =
      browserLocationsBeingApplied === 0 &&
      payload.selectedSlideId !== null &&
      loadedPresentation?._id === payload.presentationId &&
      loadedPresentation.slides?.includes(payload.selectedSlideId) &&
      payload.contextComponentId === loadedContextComponentId;
    if (canReuseLoadedPresentation) {
      dispatchAction(selectSlide(payload.selectedSlideId));
      return;
    }

    browserLocationsBeingApplied += 1;
    try {
      await dispatchActionAndWaitForResponse(
        loadPresentation(payload),
        loadPresentationSuccess,
        loadPresentationError
      );
    } finally {
      browserLocationsBeingApplied -= 1;
      if (browserLocationsBeingApplied === 0 && pendingStateLocation) {
        const location = pendingStateLocation;
        pendingStateLocation = null;
        presentationRouter.depart(location);
      }
    }
  };

  const handleNavigationError = (error: unknown): void => {
    dispatchAction(loadPresentationError({ error }));
  };

  const browserRouter = createBrowserRouterAdapter({
    router: presentationRouter,
    browserHistory,
    onArrive: loadPresentationFromLocation,
    onNavigationError: handleNavigationError,
  });

  const startRouter = async (): Promise<void> => {
    loadedPresentationSubscription = loadedPresentation$
      .pipe(
        map(state => ({
          presentation: state.presentation,
          contextComponentId: state.contextComponentId,
        })),
        distinctUntilChanged(isEqual)
      )
      .subscribe(state => {
        loadedPresentation = state.presentation;
        loadedContextComponentId = state.contextComponentId;
      });
    await browserRouter.start();
    stateSubscription = presentationStateLocation$(toStateLocation).subscribe({
      next: departOrDeferStateLocation,
      error: handleNavigationError,
    });
  };

  const teardown = (): void => {
    stateSubscription?.unsubscribe();
    stateSubscription = null;
    loadedPresentationSubscription?.unsubscribe();
    loadedPresentationSubscription = null;
    browserRouter.teardown();
  };

  return { presentationRouter, startRouter, teardown };
};

export function setupRouter(
  options: SetupRouterOptions
):
  | PresentationRouter<CustomDomainPresentationRouteTree>
  | PresentationRouter<StandardDomainPresentationRouteTree> {
  if (options.isCustomDomain) {
    return createPresentationRouter(
      {
        routeTree: getPresentationRouteTree(true),
        toStateLocation: toCustomDomainStateLocation,
      },
      options.browserHistory
    );
  }

  return createPresentationRouter(
    {
      routeTree: getPresentationRouteTree(false),
      toStateLocation: toStandardDomainStateLocation,
    },
    options.browserHistory
  );
}
